
import React, { useState, useEffect, ChangeEvent, FormEvent } from 'react';
import api from '../../services/api';
import './AppointmentForm.css';

interface Appointment {
  id: number;
  patient_id: number;
  google_event_id: string | null;
  appointment_type: string;
  date: string;
  time: string;
  status: string;
  appointment_status: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  patient_name: string;
  phone: string;
  pet_name: string;
  pet_species: string;
  pet_breed: string;
}

interface AppointmentFormProps {
  appointment: Appointment | null;
  onClose: () => void;
  onSubmit: () => void;
}

const AppointmentForm: React.FC<AppointmentFormProps> = ({ appointment, onClose, onSubmit }) => {
  const isEditMode = !!appointment;
  
  const [formData, setFormData] = useState({
    pet_owner: '',
    phone: '',
    email: '',
    pet_name: '',
    pet_species: '',
    pet_breed: '',
    pet_gender: '',
    pet_age: '',
    appointment_type: 'consultation',
    date: '',
    time: '',
    notes: ''
  });
  
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [actionMessage, setActionMessage] = useState('');
  
  // Reschedule Modal States
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [availableDatesMap, setAvailableDatesMap] = useState<Map<string, boolean>>(new Map());
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [availableTimes, setAvailableTimes] = useState<string[]>([]);
  const [rescheduleError, setRescheduleError] = useState('');
  const [rescheduleSuccess, setRescheduleSuccess] = useState('');
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [loadingCalendar, setLoadingCalendar] = useState(false);
  
  // Calendar state
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDateObj, setSelectedDateObj] = useState<Date | null>(null);

  // Appointment types for dropdown
  const appointmentTypes = ['consultation', 'vaccination', 'follow-up', 'surgery'];

  // Fetch working hours and build available dates map
  const fetchAvailableDates = async () => {
    setLoadingCalendar(true);
    try {
      const response = await api.get('/admin/working-hours');
      if (response.data.success) {
        const workingHours = response.data.data;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        const datesMap = new Map<string, boolean>();
        
        // Get open days
        const openDays = workingHours
          .filter((h: any) => h.is_open)
          .map((h: any) => h.day_of_week);
        
        // Generate next 60 days
        for (let i = 0; i <= 60; i++) {
          const futureDate = new Date(today);
          futureDate.setDate(today.getDate() + i);
          const dayName = futureDate.toLocaleDateString('en-US', { weekday: 'long' });
          const dateStr = futureDate.toISOString().split('T')[0];
          
          if (openDays.includes(dayName)) {
            datesMap.set(dateStr, true);
          }
        }
        
        setAvailableDatesMap(datesMap);
      }
    } catch (err) {
      console.error('Error fetching working hours:', err);
      setRescheduleError('Failed to load available dates');
    } finally {
      setLoadingCalendar(false);
    }
  };

  // Fetch available slots for selected date - CORRECTED API ENDPOINT
  const fetchAvailableSlots = async (date: string) => {
    setLoadingSlots(true);
    setAvailableTimes([]);
    setSelectedTime('');
    setRescheduleError('');
    
    try {
      // ✅ CORRECT API ENDPOINT - /api/admin
      const response = await api.post('/admin', {
        date: date,
        type: formData.appointment_type
      });
      
      if (response.data.success && response.data.data.available_slots) {
        setAvailableTimes(response.data.data.available_slots);
        if (response.data.data.available_slots.length === 0) {
          setRescheduleError('No available slots for this date');
        }
      } else {
        setAvailableTimes([]);
        setRescheduleError(response.data.message || 'No available slots for this date');
      }
    } catch (err) {
      console.error('Error fetching slots:', err);
      setAvailableTimes([]);
      setRescheduleError('Failed to load available slots');
    } finally {
      setLoadingSlots(false);
    }
  };

  // Handle appointment type change in reschedule modal
  const handleAppointmentTypeChange = (type: string) => {
    setFormData(prev => ({ ...prev, appointment_type: type }));
    // If a date is already selected, refetch slots with new type
    if (selectedDate) {
      fetchAvailableSlots(selectedDate);
    }
  };

  // Handle date selection from calendar
  const handleDateSelect = (date: Date) => {
    const dateStr = date.toISOString().split('T')[0];
    setSelectedDate(dateStr);
    setSelectedDateObj(date);
    setRescheduleError('');
    fetchAvailableSlots(dateStr);
  };

  // Handle time selection
  const handleTimeSelect = (time: string) => {
    setSelectedTime(time);
  };

  // Submit Reschedule
  const handleConfirmReschedule = async () => {
    if (!selectedDate || !selectedTime) {
      setRescheduleError('Please select both date and time');
      return;
    }
    
    setSaving(true);
    setRescheduleError('');
    setRescheduleSuccess('');
    
    try {
      const response = await api.post('/admin/reschedule', {
        appointment_id: appointment?.id,
        date: selectedDate,
        time: selectedTime
      });
      
      if (response.data.success) {
        setRescheduleSuccess('Appointment rescheduled successfully!');
        setTimeout(() => {
          setShowRescheduleModal(false);
          onSubmit();
          onClose();
        }, 1500);
      } else {
        setRescheduleError(response.data.message || 'Failed to reschedule');
      }
    } catch (err: any) {
      setRescheduleError(err.response?.data?.error || err.response?.data?.message || 'Error rescheduling appointment');
    } finally {
      setSaving(false);
    }
  };

  // Open reschedule modal
  const handleOpenRescheduleModal = async () => {
    setShowRescheduleModal(true);
    setRescheduleError('');
    setRescheduleSuccess('');
    setSelectedDate('');
    setSelectedTime('');
    setSelectedDateObj(null);
    setAvailableTimes([]);
    setCurrentMonth(new Date());
    await fetchAvailableDates();
  };

  // Calendar navigation
  const goToPrevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  // Generate calendar days
  const getCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDayOfMonth = new Date(year, month, 1);
    const lastDayOfMonth = new Date(year, month + 1, 0);
    const startDayOfWeek = firstDayOfMonth.getDay();
    const daysInMonth = lastDayOfMonth.getDate();
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const days: { date: Date; isCurrentMonth: boolean; isAvailable: boolean; isPast: boolean }[] = [];
    
    // Previous month days
    for (let i = startDayOfWeek - 1; i >= 0; i--) {
      const date = new Date(year, month, -i);
      days.push({
        date,
        isCurrentMonth: false,
        isAvailable: false,
        isPast: date < today
      });
    }
    
    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(year, month, i);
      const dateStr = date.toISOString().split('T')[0];
      const isPast = date < today;
      const isAvailable = !isPast && availableDatesMap.has(dateStr);
      days.push({
        date,
        isCurrentMonth: true,
        isAvailable,
        isPast
      });
    }
    
    // Next month days (to fill 6 rows = 42 days)
    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      const date = new Date(year, month + 1, i);
      days.push({
        date,
        isCurrentMonth: false,
        isAvailable: false,
        isPast: date < today
      });
    }
    
    return days;
  };

  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const calendarDays = getCalendarDays();

  // Handle Complete action
  const handleComplete = async () => {
    if (!window.confirm('Mark this appointment as completed?')) return;
    
    setSaving(true);
    setError('');
    try {
      const response = await api.post('/admin/complete', {
        appointment_id: appointment?.id,
        notes: formData.notes || 'Marked as completed by admin'
      });
      if (response.data.success) {
        setActionMessage('Appointment marked as completed!');
        setTimeout(() => {
          setActionMessage('');
          onSubmit();
          onClose();
        }, 1500);
      } else {
        setError(response.data.message || 'Failed to complete');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error completing appointment');
    } finally {
      setSaving(false);
    }
  };

  // Handle Delete action
  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this appointment?')) return;
    
    setSaving(true);
    setError('');
    try {
      // const response = await api.delete(`/admin/appointments/${appointment?.id}`);
      // const response = await api.post(`/admin/cancel/${appointment?.id}`);
      const response = await api.post('/admin/cancel', {
  appointment_id: appointment?.id
});

      if (response.data.success) {
        setActionMessage('Appointment deleted successfully!');
        setTimeout(() => {
          setActionMessage('');
          onSubmit();
          onClose();
        }, 1500);
      } else {
        setError(response.data.message || 'Failed to delete');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error deleting appointment');
    } finally {
      setSaving(false);
    }
  };

  // Handle Create/Book new appointment
  const handleBookSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    if (!formData.pet_owner || !formData.phone || !formData.pet_name || 
        !formData.pet_species || !formData.appointment_type || 
        !formData.date || !formData.time) {
      setError('Please fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        patient_name: formData.pet_owner,
        phone: formData.phone,
        email: formData.email || null,
        pet_name: formData.pet_name,
        pet_species: formData.pet_species,
        pet_breed: formData.pet_breed,
        pet_gender: formData.pet_gender || null,
        pet_age: formData.pet_age || null,
        appointment_type: formData.appointment_type,
        date: formData.date,
        time: formData.time,
        is_new_patient: true
      };
      
      const response = await api.post('/admin/appointments', payload);
      if (response.data.success) {
        onSubmit();
        onClose();
      } else {
        setError(response.data.message || 'Failed to book appointment');
      }
    } catch (err: any) {
      console.error('Error booking appointment:', err);
      setError(err.response?.data?.error || err.response?.data?.message || 'Error booking appointment');
    } finally {
      setSaving(false);
    }
  };

  const handleFormChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const getMinDate = () => {
    const today = new Date();
    today.setDate(today.getDate() + 1);
    return today.toISOString().split('T')[0];
  };

  const formatDisplayDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Edit Mode View
  if (isEditMode) {
    return (
      <>
        <div className="modal-overlay" onClick={onClose}>
          <div className="modal-content modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>✏️ Appointment Details</h2>
              <button className="close-btn" onClick={onClose}>✕</button>
            </div>

            <div className="modal-body">
              {actionMessage && <div className="success-message">{actionMessage}</div>}
              {error && <div className="form-error">{error}</div>}

              <div className="appointment-details-view">
                <div className="detail-section">
                  <h3>📋 Appointment Information</h3>
                  <div className="detail-row">
                    <span className="detail-label">Date:</span>
                    <span className="detail-value">{formatDisplayDate(formData.date)}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Time:</span>
                    <span className="detail-value">{formData.time}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Type:</span>
                    <span className="detail-value appointment-type-value">{formData.appointment_type}</span>
                  </div>
                </div>

                <div className="detail-section">
                  <h3>👤 Pet Owner</h3>
                  <div className="detail-row">
                    <span className="detail-label">Name:</span>
                    <span className="detail-value">{formData.pet_owner}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Phone:</span>
                    <span className="detail-value">{formData.phone}</span>
                  </div>
                  {formData.email && (
                    <div className="detail-row">
                      <span className="detail-label">Email:</span>
                      <span className="detail-value">{formData.email}</span>
                    </div>
                  )}
                </div>

                <div className="detail-section">
                  <h3>🐾 Pet Information</h3>
                  <div className="detail-row">
                    <span className="detail-label">Name:</span>
                    <span className="detail-value">{formData.pet_name}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Species:</span>
                    <span className="detail-value">{formData.pet_species}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Breed:</span>
                    <span className="detail-value">{formData.pet_breed}</span>
                  </div>
                  {formData.pet_gender && (
                    <div className="detail-row">
                      <span className="detail-label">Gender:</span>
                      <span className="detail-value">{formData.pet_gender}</span>
                    </div>
                  )}
                  {formData.pet_age && (
                    <div className="detail-row">
                      <span className="detail-label">Age:</span>
                      <span className="detail-value">{formData.pet_age}</span>
                    </div>
                  )}
                </div>

                {formData.notes && (
                  <div className="detail-section">
                    <h3>📝 Notes</h3>
                    <div className="detail-row">
                      <span className="detail-value">{formData.notes}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="modal-footer action-buttons-group">
              <button 
                type="button" 
                className="action-btn reschedule-btn" 
                onClick={handleOpenRescheduleModal}
                disabled={saving}
              >
                <span className="btn-icon">🔄</span>
                <span className="btn-text">Reschedule</span>
              </button>
              <button 
                type="button" 
                className="action-btn complete-btn" 
                onClick={handleComplete}
                disabled={saving}
              >
                <span className="btn-icon">✔️</span>
                <span className="btn-text">Complete</span>
              </button>
              <button 
                type="button" 
                className="action-btn delete-btn" 
                onClick={handleDelete}
                disabled={saving}
              >
                <span className="btn-icon">🗑️</span>
                <span className="btn-text">Delete</span>
              </button>
              <button 
                type="button" 
                className="action-btn close-btn-modal" 
                onClick={onClose}
              >
                <span className="btn-icon">✕</span>
                <span className="btn-text">Close</span>
              </button>
            </div>
          </div>
        </div>

        {/* Reschedule Modal with Calendar */}
        {showRescheduleModal && (
          <div className="modal-overlay" onClick={() => setShowRescheduleModal(false)}>
            <div className="reschedule-modal reschedule-modal-lg" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>🔄 Reschedule Appointment</h2>
                <button className="close-btn" onClick={() => setShowRescheduleModal(false)}>✕</button>
              </div>
              
              <div className="modal-body">
                {rescheduleSuccess && <div className="success-message">{rescheduleSuccess}</div>}
                {rescheduleError && <div className="form-error">{rescheduleError}</div>}
                
                {loadingCalendar ? (
                  <div className="loading-calendar">Loading calendar...</div>
                ) : (
                  <>
                    {/* Appointment Type Selector */}
                    <div className="form-group">
                      <label>Appointment Type</label>
                      <select
                        className="appointment-type-select"
                        value={formData.appointment_type}
                        onChange={(e) => handleAppointmentTypeChange(e.target.value)}
                      >
                        {appointmentTypes.map(type => (
                          <option key={type} value={type}>
                            {type.charAt(0).toUpperCase() + type.slice(1)}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    {/* Calendar Navigation */}
                    <div className="calendar-nav">
                      <button type="button" className="month-nav-btn" onClick={goToPrevMonth}>◀</button>
                      <span className="current-month">
                        {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                      </span>
                      <button type="button" className="month-nav-btn" onClick={goToNextMonth}>▶</button>
                    </div>
                    
                    {/* Calendar Grid */}
                    <div className="calendar-grid">
                      {weekDays.map(day => (
                        <div key={day} className="calendar-weekday">{day}</div>
                      ))}
                      {calendarDays.map((day, index) => {
                        const isSelected = selectedDateObj && 
                          day.date.toDateString() === selectedDateObj.toDateString();
                        return (
                          <div
                            key={index}
                            className={`calendar-day 
                              ${!day.isCurrentMonth ? 'other-month' : ''} 
                              ${day.isPast ? 'past' : ''} 
                              ${day.isAvailable ? 'available' : 'unavailable'}
                              ${isSelected ? 'selected' : ''}
                            `}
                            onClick={() => {
                              if (day.isAvailable && !day.isPast) {
                                handleDateSelect(day.date);
                              }
                            }}
                          >
                            <span className="day-number">{day.date.getDate()}</span>
                          </div>
                        );
                      })}
                    </div>
                    
                    {/* Selected Date Info */}
                    {selectedDateObj && (
                      <div className="selected-date-info">
                        <strong>Selected Date:</strong> {selectedDateObj.toLocaleDateString('en-US', { 
                          weekday: 'long', 
                          month: 'long', 
                          day: 'numeric', 
                          year: 'numeric' 
                        })}
                      </div>
                    )}
                    
                    {/* Time Slots */}
                    {loadingSlots && (
                      <div className="loading-slots">Loading available times...</div>
                    )}
                    
                    {availableTimes.length > 0 && !loadingSlots && (
                      <div className="time-slots-section">
                        <label>Select Time Slot</label>
                        <div className="time-slots-grid">
                          {availableTimes.map(time => (
                            <button
                              key={time}
                              type="button"
                              className={`time-slot-btn ${selectedTime === time ? 'selected' : ''}`}
                              onClick={() => handleTimeSelect(time)}
                            >
                              {time}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {!loadingSlots && selectedDate && availableTimes.length === 0 && !rescheduleError && (
                      <div className="info-message">No available slots for this date. Please select another date.</div>
                    )}
                  </>
                )}
              </div>
              
              <div className="modal-footer">
                <button type="button" className="cancel-btn" onClick={() => setShowRescheduleModal(false)}>
                  Cancel
                </button>
                <button 
                  type="button"
                  className="save-btn" 
                  onClick={handleConfirmReschedule}
                  disabled={!selectedDate || !selectedTime || saving}
                >
                  {saving ? 'Rescheduling...' : 'Confirm Reschedule'}
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  // New Appointment Mode
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-lg" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>📅 Book New Appointment</h2>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>

        <form onSubmit={handleBookSubmit}>
          <div className="modal-body">
            {error && <div className="form-error">{error}</div>}

            <div className="form-section">
              <h3>📋 Appointment Details</h3>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="date">Appointment Date *</label>
                  <input
                    type="date"
                    id="date"
                    name="date"
                    value={formData.date}
                    onChange={handleFormChange}
                    min={getMinDate()}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="time">Appointment Time *</label>
                  <input
                    type="time"
                    id="time"
                    name="time"
                    value={formData.time}
                    onChange={handleFormChange}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="appointment_type">Appointment Type *</label>
                <select
                  id="appointment_type"
                  name="appointment_type"
                  value={formData.appointment_type}
                  onChange={handleFormChange}
                  required
                >
                  <option value="consultation">Consultation</option>
                  <option value="vaccination">Vaccination</option>
                  <option value="follow-up">Follow-up</option>
                  <option value="surgery">Surgery</option>
                </select>
              </div>
            </div>

            <div className="form-section">
              <h3>👤 Pet Owner Information</h3>
              
              <div className="form-group">
                <label htmlFor="pet_owner">Pet Owner Name *</label>
                <input
                  type="text"
                  id="pet_owner"
                  name="pet_owner"
                  value={formData.pet_owner}
                  onChange={handleFormChange}
                  placeholder="Full name of pet owner"
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="phone">Phone Number *</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleFormChange}
                    placeholder="+1XXXXXXXXXX"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleFormChange}
                    placeholder="Optional"
                  />
                </div>
              </div>
            </div>

            <div className="form-section">
              <h3>🐾 Pet Information</h3>
              
              <div className="form-group">
                <label htmlFor="pet_name">Pet Name *</label>
                <input
                  type="text"
                  id="pet_name"
                  name="pet_name"
                  value={formData.pet_name}
                  onChange={handleFormChange}
                  placeholder="Pet's name"
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="pet_species">Species *</label>
                  <input
                    type="text"
                    id="pet_species"
                    name="pet_species"
                    value={formData.pet_species}
                    onChange={handleFormChange}
                    placeholder="e.g., Dog, Cat"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="pet_breed">Breed *</label>
                  <input
                    type="text"
                    id="pet_breed"
                    name="pet_breed"
                    value={formData.pet_breed}
                    onChange={handleFormChange}
                    placeholder="e.g., Labrador"
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="pet_gender">Gender</label>
                  <select
                    id="pet_gender"
                    name="pet_gender"
                    value={formData.pet_gender}
                    onChange={handleFormChange}
                  >
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="pet_age">Age</label>
                  <input
                    type="text"
                    id="pet_age"
                    name="pet_age"
                    value={formData.pet_age}
                    onChange={handleFormChange}
                    placeholder="e.g., 3 years, 6 months"
                  />
                </div>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="notes">Notes (Optional)</label>
              <textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleFormChange}
                placeholder="Any additional notes..."
                rows={3}
              />
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="cancel-btn" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="save-btn" disabled={saving}>
              {saving ? 'Booking...' : 'Book Appointment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AppointmentForm;